import { useEffect, useState } from "react";
import { useLocation, Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger } from "@/components/ui/sheet";
import { HelpCircle, ExternalLink, BookOpen } from "lucide-react";

import { tr } from "@/lib/tr";
type Lang = "ar" | "en" | "fr";

// Convert "/sales/invoices/123/edit" -> patterns to try, most specific first
function buildPatterns(pathname: string): string[] {
  const segments = pathname.split("/").filter(Boolean);
  const patterns: string[] = [pathname];
  // Replace UUID-ish or numeric segments with :id
  const generic = "/" + segments.map((s) => (/^[0-9a-f-]{8,}$/i.test(s) ? ":id" : s)).join("/");
  if (generic !== pathname) patterns.push(generic);
  // Progressively shorter parent paths
  for (let i = segments.length - 1; i >= 1; i--) {
    patterns.push("/" + segments.slice(0, i).join("/"));
  }
  return Array.from(new Set(patterns));
}

export function HelpFab() {
  const { pathname } = useLocation();
  const { i18n } = useTranslation();
  const lang = (i18n.language as Lang) || "ar";
  const isAr = lang === "ar";
  const [open, setOpen] = useState(false);

  const patterns = buildPatterns(pathname);

  const { data: contextual } = useQuery({
    queryKey: ["help-fab", pathname],
    queryFn: async () => {
      const { data } = await (supabase as any)
        .from("help_articles")
        .select("id, slug, route_pattern, help_article_translations(lang, title, summary)")
        .eq("status", "published")
        .in("route_pattern", patterns);
      // Prefer most specific (longest pattern) match
      const sorted = (data || []).slice().sort(
        (a: any, b: any) => (b.route_pattern?.length || 0) - (a.route_pattern?.length || 0),
      );
      return sorted as any[];
    },
  });

  const { data: latest } = useQuery({
    queryKey: ["help-fab-latest"],
    enabled: open,
    queryFn: async () => {
      const { data } = await (supabase as any)
        .from("help_articles")
        .select("id, slug, help_article_translations(lang, title)")
        .eq("status", "published")
        .order("created_at", { ascending: false })
        .limit(8);
      return data || [];
    },
  });

  const pick = (rows?: any[]) =>
    rows?.find((r) => r.lang === lang) || rows?.find((r) => r.lang === "ar") || rows?.[0];

  return (
    <Sheet open={open} onOpenChange={setOpen}>
      <SheetTrigger asChild>
        <Button
          variant="ghost"
          size="icon"
          className="shrink-0 print:hidden"
          aria-label={tr("مساعدة", "Help", "Aide")}
        >
          <HelpCircle className="h-4 w-4" />
        </Button>
      </SheetTrigger>
      <SheetContent side={(tr("left", "right", "right") as "left" | "right")} className="w-full sm:max-w-md overflow-y-auto">
        <SheetHeader>
          <SheetTitle className="font-display">
            {tr("كيف نساعدك؟", "How can we help?", "Comment pouvons-nous vous aider ?")}
          </SheetTitle>
        </SheetHeader>

        <div className="mt-6 space-y-6">
          {contextual && contextual.length > 0 && (
            <section>
              <h3 className="text-xs font-semibold text-muted-foreground uppercase mb-2">
                {tr("مرتبط بهذه الصفحة", "Related to this page", "Lié à cette page")}
              </h3>
              <ul className="space-y-2">
                {contextual.map((a: any) => {
                  const t = pick(a.help_article_translations);
                  return (
                    <li key={a.id}>
                      <Link
                        to={`/help/${a.slug}`}
                        target="_blank"
                        onClick={() => setOpen(false)}
                        className="flex items-start gap-2 rounded-md border border-border p-3 hover:border-primary hover:bg-muted/30 transition"
                      >
                        <BookOpen className="h-4 w-4 mt-0.5 text-primary shrink-0" />
                        <div className="min-w-0 flex-1">
                          <div className="font-medium text-sm">{t?.title || a.slug}</div>
                          {t?.summary && (
                            <div className="text-xs text-muted-foreground line-clamp-2 mt-0.5">
                              {t.summary}
                            </div>
                          )}
                        </div>
                      </Link>
                    </li>
                  );
                })}
              </ul>
            </section>
          )}

          <section>
            <h3 className="text-xs font-semibold text-muted-foreground uppercase mb-2">
              {tr("أحدث المقالات", "Latest articles", "Derniers articles")}
            </h3>
            <ul className="space-y-1.5">
              {(latest || []).map((a: any) => {
                const t = pick(a.help_article_translations);
                return (
                  <li key={a.id}>
                    <Link
                      to={`/help/${a.slug}`}
                      target="_blank"
                      onClick={() => setOpen(false)}
                      className="flex items-center gap-2 text-sm py-1.5 hover:text-primary"
                    >
                      <span className="truncate">{t?.title || a.slug}</span>
                    </Link>
                  </li>
                );
              })}
            </ul>
          </section>

          <Button asChild variant="outline" className="w-full">
            <Link to="/help" target="_blank" onClick={() => setOpen(false)}>
              <ExternalLink className="h-4 w-4 me-2" />
              {tr("فتح مركز المساعدة", "Open Help Center", "Ouvrir le centre d'aide")}
            </Link>
          </Button>
        </div>
      </SheetContent>
    </Sheet>
  );
}
